import React, { useCallback, useEffect, useMemo, useState } from "react";
import { emitToast } from "./Toast";
import { WD, ymd, ymdParts, hm, formatJaDate } from "./dateUtil";
import useEscapeKey from "./useEscapeKey";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// ホームのカレンダー（会議2026-07・案Q）。iPhoneカレンダー風の月グリッド＋選択日の予定リスト＋追加。
// カレンダー専用画面をホームに寄せる第一歩。予定の追加は PC=中央モーダル / スマホ=下からシート（CSSで切替）。
// データは既存の /api/employee/calendar/（共有カレンダー）。
const API_URL = process.env.REACT_APP_API_URL || "";
// 終日判定：allDay フラグ or 日付のみ文字列（T無し）。
const isAllDay = (e) => e.allDay === true || (typeof e.start === "string" && !e.start.includes("T"));
// 予定の日付キー（TZずれを避けるため文字列先頭を優先）。
const eventDate = (e) => (typeof e.start === "string" && e.start.length >= 10 ? e.start.slice(0, 10) : ymd(new Date(e.start)));
// 予定の終了日キー（end 無しは開始日と同じ＝単日）。
const eventEndDate = (e) => {
  if (!e.end) return eventDate(e);
  return typeof e.end === "string" && e.end.length >= 10 ? e.end.slice(0, 10) : ymd(new Date(e.end));
};
// 連日（複数日にまたがる）予定か。
const isMultiDay = (e) => eventEndDate(e) > eventDate(e);

const EMPTY_FORM = { title: "", allday: false, start: "", end: "", repeat: false, weekdays: [], repeatStart: "", untilOn: false, until: "" };

// 曜日コード（iCalendar の BYDAY と同じ綴り）。並びは日曜始まり＝カレンダーの見た目に合わせる。
// 数値(0=日/1=月…)で持つと Python の weekday()（月=0）と食い違うため、綴りで揃えている。
const WD_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const WD_JA = { SU: "日", MO: "月", TU: "火", WE: "水", TH: "木", FR: "金", SA: "土" };
// "YYYY-MM-DD" → その日の曜日コード。
const codeOf = (ds) => {
  const [y, m, d] = String(ds).split("-").map(Number);
  return WD_CODES[new Date(y, m - 1, d).getDay()];
};
// 保存用の "MO,WE,FR"（月→日の順。BE と同じ並びに揃えて表示のブレを無くす）。
const ICS_ORDER = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
const sortCodes = (codes) => ICS_ORDER.filter((c) => codes.includes(c));

// くり返しを展開して取得する期間（基準日の3ヶ月前〜12ヶ月後）。BEの既定と同じ幅にしておく。
const shiftMonths = (d, n) => { const x = new Date(d.getFullYear(), d.getMonth() + n, 1); return ymd(x); };
const windowStart = (d) => shiftMonths(d, -3);
const windowEnd = (d) => shiftMonths(d, 12);
// 連日帯1レーンの高さ（px）。セル側の予約スペース（bandpad）と揃える。
const BAND_ROW = 15;

function HomeCalendar() {
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState({}); // { "YYYY-MM-DD": "祝日名" }（ロスターと同源）
  const today = ymd(new Date());
  const [sel, setSel] = useState(today);
  const [view, setView] = useState({ y: new Date().getFullYear(), m: new Date().getMonth() + 1 });
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null); // null=新規追加 / それ以外=編集中の予定ID
  // 更新・削除の宛先。くり返しの各回は id が「元ID@日付」で実体が無いため、必ず元ルールのIDを送る。
  const [editSeriesId, setEditSeriesId] = useState(null);
  const [icsUrl, setIcsUrl] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  // 初期表示は選択グレーを出さない。ユーザーが日をクリックして初めて点灯（本日の予定リストは既定で表示）。
  const [touched, setTouched] = useState(false);

  // 取得済みの期間。毎週くり返す予定はBEが「この期間ぶん」だけ各回に展開して返すため、
  // 窓の外へ月送りするとくり返しだけ消えて見える。窓を出たら取り直す（下の useEffect）。
  const [loaded, setLoaded] = useState({ from: "", to: "" });

  const fetchEvents = useCallback(async (from, to) => {
    const [f, t] = [from || windowStart(new Date()), to || windowEnd(new Date())];
    try {
      // 確定(confirmed)のみ表示：ホームの「今日の予定」・ICS配信と範囲を揃える（未確定 draft は出さない・#13）。
      const res = await fetch(
        `${API_URL}/api/employee/calendar/?status=confirmed&from=${f}&to=${t}`,
        { credentials: "include" },
      );
      if (res.ok) {
        setEvents((await res.json()).results || []);
        setLoaded({ from: f, to: t });
      }
    } catch (e) { /* 補助表示なので失敗は握りつぶす */ }
  }, []);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // 祝日＋会社休（ロスターと同じ /api/employee/leave/unavailable の holidays を使用）。
  const fetchHolidays = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/employee/leave/unavailable/`, { credentials: "include" });
      if (res.ok) setHolidays((await res.json()).holidays || {});
    } catch (e) { /* 補助表示なので失敗は握りつぶす */ }
  }, []);
  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  // 表示中の月が取得済み期間から出たら、その月を中心に取り直す。
  // これが無いと、月送りを続けたときに単発の予定だけ残り、毎週の予定が消えたように見える。
  useEffect(() => {
    if (!loaded.from) return;
    const first = ymdParts(view.y, view.m, 1);
    if (first >= loaded.from && first <= loaded.to) return;
    const base = new Date(view.y, view.m - 1, 1);
    fetchEvents(windowStart(base), windowEnd(base));
  }, [view, loaded, fetchEvents]);

  // Esc で追加シートを閉じる。
  useEscapeKey(() => setAddOpen(false), addOpen);

  // 単日予定と連日予定を分離。単日はセル内チップ、連日は週をまたぐ帯（Mac風）で描く。
  const singleEvents = useMemo(() => events.filter((e) => !isMultiDay(e)), [events]);
  const multiEvents = useMemo(() => events.filter(isMultiDay), [events]);

  // 日付キー→単日予定（開始順）。セル内に最大2件のチップで表示するため。
  const eventsByDay = useMemo(() => {
    const m = new Map();
    singleEvents.forEach((e) => {
      const k = eventDate(e);
      const arr = m.get(k) || [];
      arr.push(e);
      m.set(k, arr);
    });
    m.forEach((arr) => arr.sort((a, b) => String(a.start).localeCompare(String(b.start))));
    return m;
  }, [singleEvents]);

  // 選択日の予定リスト（連日予定はその期間の各日で表示されるよう範囲判定）。
  const selList = useMemo(
    () => events
      .filter((e) => sel >= eventDate(e) && sel <= eventEndDate(e))
      .sort((a, b) => String(a.start).localeCompare(String(b.start))),
    [events, sel],
  );

  // 週ごとのセル日付＋連日帯（レーン割付済み）。Mac風に週をまたぐ帯を分割配置する。
  const weeks = useMemo(() => {
    const firstWeekday = new Date(view.y, view.m - 1, 1).getDay();
    const gridStart = new Date(view.y, view.m - 1, 1 - firstWeekday);
    const total = firstWeekday + new Date(view.y, view.m, 0).getDate();
    const weekCount = Math.ceil(total / 7);
    const out = [];
    for (let w = 0; w < weekCount; w += 1) {
      const cols = [];
      for (let c = 0; c < 7; c += 1) {
        const dt = new Date(gridStart);
        dt.setDate(gridStart.getDate() + w * 7 + c);
        cols.push({ day: dt.getMonth() === view.m - 1 ? dt.getDate() : null, ds: ymd(dt) });
      }
      const wStart = cols[0].ds;
      const wEnd = cols[6].ds;
      // この週に交差する連日予定を、週の範囲に左右クリップ。
      const segs = [];
      multiEvents.forEach((e) => {
        const s = eventDate(e);
        const en = eventEndDate(e);
        if (en < wStart || s > wEnd) return;
        const startCol = s <= wStart ? 0 : cols.findIndex((c) => c.ds === s);
        const endCol = en >= wEnd ? 6 : cols.findIndex((c) => c.ds === en);
        segs.push({
          event: e,
          startCol: startCol < 0 ? 0 : startCol,
          endCol: endCol < 0 ? 6 : endCol,
          roundL: s >= wStart, // 本当の開始がこの週 → 左端を丸める
          roundR: en <= wEnd, // 本当の終了がこの週 → 右端を丸める
        });
      });
      // レーン割付（開始列昇順→長い順の貪欲法。列が重なる帯は別レーンへ積む）。
      segs.sort((a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol));
      const laneEnds = [];
      const bands = segs.map((seg) => {
        let lane = 0;
        while (lane < laneEnds.length && laneEnds[lane] >= seg.startCol) lane += 1;
        laneEnds[lane] = seg.endCol;
        return { ...seg, lane, key: `${seg.event.id}-${w}` };
      });
      out.push({ cols, laneCount: laneEnds.length, bands });
    }
    return out;
  }, [view, multiEvents]);

  const prevM = () => setView((v) => (v.m === 1 ? { y: v.y - 1, m: 12 } : { y: v.y, m: v.m - 1 }));
  const nextM = () => setView((v) => (v.m === 12 ? { y: v.y + 1, m: 1 } : { y: v.y, m: v.m + 1 }));
  const goToday = () => { setView({ y: new Date().getFullYear(), m: new Date().getMonth() + 1 }); setSel(today); setTouched(true); };

  // 追加シートを開く。既定で 09:00〜10:00（空だと time 入力が --:-- で薄く見えるため）。
  // くり返しは既定オフ。オンにしたときの曜日は選択中の日の曜日を初期値にする（Mac のカレンダーと同じ）。
  const openAdd = () => {
    setEditId(null);
    setEditSeriesId(null);
    setForm({ ...EMPTY_FORM, start: "09:00", end: "10:00", weekdays: [codeOf(sel)], repeatStart: sel });
    setAddOpen(true);
  };

  // 予定行タップで同じシートを「編集」モードで開く（時刻は HH:MM に変換して初期値化）。
  // 連日予定は本シートでは期間を編集できない（単日UIのため）。時刻欄を隠し、保存時は元の期間を保持する。
  // くり返しの予定は「各回」ではなく元のルールを編集する（保存も削除もくり返し全体に効く）。
  const openEdit = (e) => {
    const allday = isMultiDay(e) || isAllDay(e);
    setEditId(e.id);
    setEditSeriesId(e.seriesId != null ? e.seriesId : e.id);
    setForm({
      title: e.title || "",
      allday,
      start: allday ? "" : hm(e.start),
      end: allday || !e.end ? "" : hm(e.end),
      repeat: !!e.repeat,
      weekdays: e.repeatWeekdays ? e.repeatWeekdays.split(",") : [codeOf(eventDate(e))],
      repeatStart: e.seriesStart || eventDate(e),
      untilOn: !!e.repeatUntil,
      until: e.repeatUntil || "",
    });
    setAddOpen(true);
  };

  // 曜日チップの入切。最後の1つは外させない（曜日ゼロのくり返しは成立しないため）。
  const toggleWeekday = (code) => setForm((p) => {
    const on = p.weekdays.includes(code);
    if (on && p.weekdays.length === 1) return p;
    return { ...p, weekdays: on ? p.weekdays.filter((c) => c !== code) : [...p.weekdays, code] };
  });

  // 追加／編集の共通送信。editId があれば update、なければ create。
  const submit = async () => {
    if (!form.title.trim()) return;
    const editing = editId != null;
    setSubmitting(true);
    // 連日予定を編集する場合は期間を保持（本シートは単日UIのため潰さない）。
    const orig = editing ? events.find((e) => e.id === editId) : null;
    const origMulti = !!orig && isMultiDay(orig);
    // くり返しは選択日ではなく「くり返しの開始日」を基準にする（開始日をずらせるようにするため）。
    const baseDate = form.repeat ? (form.repeatStart || sel) : sel;
    // 終日は日付のみ、通常は基準日＋時刻。end は任意。
    const start = origMulti ? orig.start : (form.allday ? baseDate : `${baseDate}T${form.start || "09:00"}`);
    const end = origMulti ? (orig.end || "") : (form.allday ? "" : (form.end ? `${baseDate}T${form.end}` : ""));
    const alldayFlag = origMulti ? (orig.allDay === true) : form.allday;
    try {
      const res = await fetch(`${API_URL}/api/employee/calendar/${editing ? "update" : "create"}/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({
          ...(editing ? { id: editSeriesId } : {}),
          title: form.title.trim(), start, end, allday: alldayFlag,
          // くり返しオフのときは空文字を送る＝解除（BE 側で終了日も一緒に消える）。
          repeat_weekdays: form.repeat ? sortCodes(form.weekdays).join(",") : "",
          repeat_until: form.repeat && form.untilOn ? form.until : null,
        }),
      });
      if (!res.ok) {
        emitToast(await apiErrorMessage(res, editing ? "予定の修正に失敗しました。" : "予定の追加に失敗しました。"), "error");
        return;
      }
      emitToast(editing ? "予定を修正しました。" : "予定を追加しました。");
      setAddOpen(false);
      await fetchEvents();
    } catch (e) {
      emitToast(editing ? "予定の修正に失敗しました。" : "予定の追加に失敗しました。", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // 編集中の予定を削除（共有カレンダーの reject）。
  // くり返しはルールが1行なので、この削除はくり返し全体に効く（1回だけ消すことはできない）。
  const removeEvent = async () => {
    if (editSeriesId == null) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/employee/calendar/reject/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ id: editSeriesId }),
      });
      if (!res.ok) { emitToast(await apiErrorMessage(res, "予定の削除に失敗しました。"), "error"); return; }
      emitToast(form.repeat ? "くり返しの予定を削除しました。" : "予定を削除しました。");
      setAddOpen(false);
      await fetchEvents();
    } catch (e) {
      emitToast("予定の削除に失敗しました。", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // カレンダー購読（.ics）URL を発行して表示する。
  const issueIcs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employee/calendar/ics-token/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(), body: "{}",
      });
      if (!res.ok) { emitToast(await apiErrorMessage(res, "購読URLの発行に失敗しました。"), "error"); return; }
      const data = await res.json();
      setIcsUrl(`${API_URL || window.location.origin}${data.path}`);
    } catch (e) {
      emitToast("購読URLの発行に失敗しました。", "error");
    }
  };

  // 購読URLをコピー。clipboard API は https/localhost 以外では使えないので、
  // 失敗時は入力欄から手動でコピーしてもらう案内に落とす。
  const copyIcs = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      emitToast("購読URLをコピーしました。");
    } catch (e) {
      emitToast("コピーできませんでした。URLを選択して手動でコピーしてください。", "error");
    }
  };

  const selLabel = formatJaDate(sel);

  // 「毎週 月・水・金 17:00〜18:00（2026-12-31まで）」。保存前に結果を確かめるための一行。
  const repeatSummary = useMemo(() => {
    const days = sortCodes(form.weekdays).map((c) => WD_JA[c]).join("・");
    if (!days) return "";
    const time = form.allday ? "" : ` ${form.start || "09:00"}${form.end ? `〜${form.end}` : ""}`;
    const until = form.untilOn && form.until ? `（${form.until} まで）` : "（終わりを決めない）";
    return `毎週 ${days}${time}${until}`;
  }, [form.weekdays, form.allday, form.start, form.end, form.untilOn, form.until]);

  return (
    <div className="mp-hcal">
      <div className="mp-hcal-head">
        <div className="mp-hcal-title">{view.y}年{view.m}月</div>
        <div className="mp-hcal-nav">
          <button type="button" aria-label="前の月" onClick={prevM}>‹</button>
          <button type="button" className="mp-hcal-today" onClick={goToday}>今日</button>
          <button type="button" aria-label="次の月" onClick={nextM}>›</button>
        </div>
      </div>

      <div className="mp-hcal-grid mp-hcal-wdrow">
        {WD.map((w, i) => (
          <div key={w} className={`mp-hcal-wd${i === 0 ? " mp-sun" : ""}${i === 6 ? " mp-sat" : ""}`}>{w}</div>
        ))}
      </div>
      <div className="mp-hcal-cal">
        {weeks.map((wk, wi) => (
          <div className="mp-hcal-week" key={wi}>
            {wk.laneCount > 0 && (
              <div className="mp-hcal-bandlayer">
                {wk.bands.map((b) => (
                  <button
                    type="button"
                    key={b.key}
                    className={`mp-hcal-band${b.roundL ? " mp-bl" : ""}${b.roundR ? " mp-br" : ""}`}
                    style={{ gridColumn: `${b.startCol + 1} / span ${b.endCol - b.startCol + 1}`, gridRow: b.lane + 1 }}
                    onClick={(e) => { e.stopPropagation(); openEdit(b.event); }}
                    title={b.event.title}
                  >
                    {b.roundL && <span className="mp-hcal-bandttl">{b.event.title}</span>}
                  </button>
                ))}
              </div>
            )}
            <div className="mp-hcal-grid">
              {wk.cols.map((col, ci) => {
                if (col.day == null) return <div key={ci} className="mp-hcal-cell mp-hcal-empty" />;
                const ds = col.ds;
                const isSel = touched && ds === sel;
                const isToday = ds === today;
                const dayEvents = eventsByDay.get(ds) || [];
                const holName = holidays[ds];
                const wd = new Date(view.y, view.m - 1, col.day).getDay(); // 0=日, 6=土
                // 祝日は赤（土日色より優先）。祝日でなければ日=赤／土=青。
                const colorCls = holName ? " mp-c-hol" : wd === 0 ? " mp-c-sun" : wd === 6 ? " mp-c-sat" : "";
                return (
                  <button
                    key={ci}
                    type="button"
                    className={`mp-hcal-cell${isSel ? " mp-sel" : ""}${isToday ? " mp-hcal-td" : ""}${colorCls}`}
                    onClick={() => { setSel(ds); setTouched(true); }}
                  >
                    <span className="mp-hcal-num">{col.day}</span>
                    {wk.laneCount > 0 && (
                      <span className="mp-hcal-bandpad" aria-hidden="true" style={{ height: wk.laneCount * BAND_ROW }} />
                    )}
                    {holName && <span className="mp-hcal-holname">{holName}</span>}
                    {dayEvents.length > 0 && (
                      <span className="mp-hcal-evs">
                        {dayEvents.slice(0, 2).map((e) => (
                          <span className="mp-hcal-chip" key={e.id}>
                            {!isAllDay(e) && <span className="mp-hcal-ctime">{hm(e.start)}</span>}
                            <span className="mp-hcal-ctitle">{e.repeat && <span className="mp-hcal-rep-ic" aria-hidden="true">↻</span>}{e.title}</span>
                          </span>
                        ))}
                        {dayEvents.length > 2 && <span className="mp-hcal-more">他{dayEvents.length - 2}件</span>}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mp-hcal-day">
        <div className="mp-hcal-dayhead">
          <span className="mp-hcal-daylabel">{selLabel}の予定</span>
          <button type="button" className="mp-hcal-add" onClick={openAdd}>＋ 予定を追加</button>
        </div>
        {selList.length === 0 ? (
          <div className="mp-hcal-noev">予定はありません</div>
        ) : (
          selList.map((e) => (
            <button type="button" className="mp-hcal-ev" key={e.id} onClick={() => openEdit(e)}>
              <span className="mp-hcal-evtime">{isAllDay(e) ? "終日" : hm(e.start)}</span>
              <span className="mp-hcal-evbar" />
              <span className="mp-hcal-evtitle">{e.title}</span>
              {e.repeat && <span className="mp-hcal-evrep" title="毎週くり返す予定">↻ 毎週</span>}
            </button>
          ))
        )}
      </div>

      {/* カレンダー購読（.ics）。発行した URL を Google/Apple 等のカレンダーに貼り付けて購読する。
          URL をブラウザに直接入れても開けない（＝手順を画面に出さないと誰も使えない）ので、
          発行後は必ず貼り付け先の手順をそのまま表示する。 */}
      <div className="mp-hcal-ics">
        {icsUrl ? (
          <>
            <div className="mp-hcal-icsrow">
              <input className="mp-input mp-hcal-icsurl" readOnly value={icsUrl} onFocus={(e) => e.target.select()} />
              <button type="button" className="mp-hcal-icscopy" onClick={copyIcs}>コピー</button>
            </div>
            <p className="mp-hcal-icshelp">
              このURLはブラウザで開くものではありません。カレンダーアプリに登録すると、確定した予定が自動で表示されます。
            </p>
            <ul className="mp-hcal-icssteps">
              <li>Googleカレンダー（PC）：「他のカレンダー」の＋ →「URLで追加」に貼り付け</li>
              <li>iPhone：設定 → カレンダー → アカウント → アカウントを追加 → その他 →「照会するカレンダーを追加」に貼り付け</li>
            </ul>
          </>
        ) : null}
        <button type="button" className="mp-hcal-icsbtn" onClick={issueIcs}>{icsUrl ? "購読URLを再表示" : "カレンダーを購読（.ics）"}</button>
      </div>

      {/* 予定追加／編集：PC=中央モーダル / スマホ=下からシート（CSSで出し分け）。 */}
      {addOpen && (
        <div className="mp-hcal-back" onClick={() => setAddOpen(false)}>
          <div className="mp-hcal-sheet" role="dialog" aria-modal="true" aria-label="予定を追加" onClick={(e) => e.stopPropagation()}>
            <div className="mp-hcal-handle" />
            <div className="mp-hcal-sheettitle">
              {editId != null ? (form.repeat ? "くり返しの予定を編集" : "予定を編集") : "予定を追加"}
              {/* くり返しは日付が下の「開始日」で決まるので、選択日を出すとどちらが効くのか分からなくなる。
                  編集中だけ「全体に効く」ことを添え、新規追加では何も出さない。 */}
              <span className="mp-hcal-sheetsub">
                {form.repeat ? (editId != null ? "毎回に反映されます" : "") : selLabel}
              </span>
            </div>
            <input className="mp-hcal-titlein" placeholder="タイトルを入力" autoFocus value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
            <div className="mp-hcal-fgrp">
              <div className="mp-hcal-frow">
                <span className="mp-hcal-k">終日</span>
                <button
                  type="button"
                  className={`mp-hcal-sw${form.allday ? " mp-on" : ""}`}
                  role="switch"
                  aria-checked={form.allday}
                  aria-label="終日"
                  onClick={() => setForm((p) => ({ ...p, allday: !p.allday }))}
                ><i /></button>
              </div>
              {!form.allday && (
                <>
                  <label className="mp-hcal-frow">
                    <span className="mp-hcal-k">開始</span>
                    <input type="time" value={form.start} onChange={(e) => setForm((p) => ({ ...p, start: e.target.value }))} />
                  </label>
                  <label className="mp-hcal-frow">
                    <span className="mp-hcal-k">終了</span>
                    <input type="time" value={form.end} onChange={(e) => setForm((p) => ({ ...p, end: e.target.value }))} />
                  </label>
                </>
              )}
              {/* 毎週のくり返し。オンにすると曜日と期間が開く（オフのときは行1つぶんしか増やさない）。 */}
              <div className="mp-hcal-frow">
                <span className="mp-hcal-k">毎週くり返す</span>
                <button
                  type="button"
                  className={`mp-hcal-sw${form.repeat ? " mp-on" : ""}`}
                  role="switch"
                  aria-checked={form.repeat}
                  aria-label="毎週くり返す"
                  onClick={() => setForm((p) => ({ ...p, repeat: !p.repeat }))}
                ><i /></button>
              </div>
              {form.repeat && (
                <div className="mp-hcal-rep">
                  <div className="mp-hcal-wdpick" role="group" aria-label="くり返す曜日">
                    {WD_CODES.map((c, i) => (
                      <button
                        type="button"
                        key={c}
                        className={`mp-hcal-wdchip${form.weekdays.includes(c) ? " mp-on" : ""}${i === 0 ? " mp-sun" : ""}${i === 6 ? " mp-sat" : ""}`}
                        aria-pressed={form.weekdays.includes(c)}
                        onClick={() => toggleWeekday(c)}
                      >{WD_JA[c]}</button>
                    ))}
                  </div>
                  <label className="mp-hcal-frow">
                    <span className="mp-hcal-k">開始日</span>
                    <input type="date" value={form.repeatStart} onChange={(e) => setForm((p) => ({ ...p, repeatStart: e.target.value }))} />
                  </label>
                  <div className="mp-hcal-frow">
                    <span className="mp-hcal-k">終了日</span>
                    <div className="mp-hcal-seg">
                      <button type="button" className={form.untilOn ? "" : "mp-on"} onClick={() => setForm((p) => ({ ...p, untilOn: false }))}>ずっと</button>
                      <button type="button" className={form.untilOn ? "mp-on" : ""} onClick={() => setForm((p) => ({ ...p, untilOn: true, until: p.until || p.repeatStart }))}>日付で終わる</button>
                    </div>
                  </div>
                  {form.untilOn && (
                    <label className="mp-hcal-frow">
                      <span className="mp-hcal-k">この日まで</span>
                      <input type="date" value={form.until} min={form.repeatStart} onChange={(e) => setForm((p) => ({ ...p, until: e.target.value }))} />
                    </label>
                  )}
                </div>
              )}
            </div>
            {/* 保存すると何が起きるかを一行で見せる（曜日チップだけだと結果を想像しづらいため）。 */}
            {form.repeat && <div className="mp-hcal-repsum">{repeatSummary}</div>}
            {editId != null && (
              <button type="button" className="mp-btn mp-danger mp-hcal-del" disabled={submitting} onClick={removeEvent}>
                {form.repeat ? "くり返しをすべて削除" : "この予定を削除"}
              </button>
            )}
            <div className="mp-hcal-sheetbtns">
              <button className="mp-btn mp-ghost" onClick={() => setAddOpen(false)}>キャンセル</button>
              <button className="mp-btn mp-primary" disabled={submitting || !form.title.trim()} onClick={submit}>{submitting ? "保存中…" : "保存"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeCalendar;
